'use client';

import Link from 'next/link';
import { Icon } from '@iconify/react';
import { cn } from '../../lib/utils';
import Image from 'next/image';

import { useTranslation } from 'react-i18next';

interface ProjectCardProps {
  title: string;
  description?: string;
  href: string;
  icon: string;
  imageSrc?: string;
  className?: string;
  tags?: string[];
  translationKey?: string;
}

/**
 * Project card component
 * Displays a project with title, description, icon, and technology tags
 * Enhanced with professional design, hover effects, and subtle animations
 * Uses Iconify for high-quality, scalable icons
 */
export function ProjectCard({
  title,
  description,
  href,
  icon,
  imageSrc,
  className,
  tags,
  translationKey
}: ProjectCardProps) {
  const { t } = useTranslation();
  return (
    <Link
      href={href}
      className={cn(
        'group block rounded-xl border border-slate-200/50 dark:border-slate-700/60',
        'bg-white dark:bg-card backdrop-blur-sm',
        'transition-all duration-500 ease-out',
        'hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-black/40',
        'h-full flex flex-col relative overflow-hidden',
        'hover:border-slate-300/80 dark:hover:border-border/80',
        'hover:translate-y-[-2px]',
        className
      )}
    >
      {/* Card background with gradient and pattern */}
      <div className="absolute inset-0 bg-white dark:bg-card z-0" />
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06] bg-grid-pattern pointer-events-none z-0" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.01] via-transparent to-primary/[0.03] dark:from-primary/[0.01] dark:to-primary/[0.05] opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out pointer-events-none z-0" />

      {/* Card content */}
      <div className="flex flex-col h-full relative z-10">
        {imageSrc && (
          <div className="relative w-full h-40 overflow-hidden rounded-t-xl group-hover:h-36 transition-all duration-500 ease-out">
            <Image
              src={imageSrc}
              alt={title}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-110"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent opacity-60" />

            {/* Icon overlay when image exists */}
            <div className="absolute top-3 left-3 h-8 w-8 rounded-full bg-white/90 dark:bg-slate-800/90 flex items-center justify-center shadow-lg backdrop-blur-md transform group-hover:scale-110 transition-transform duration-500 ease-out z-20">
              <Icon icon={icon} className="h-4 w-4 text-primary dark:text-primary/90" />
            </div>
          </div>
        )}

        <div className="flex flex-col p-5 grow">
          {/* Icon header - only show if no imageSrc */}
          {!imageSrc && (
            <div className="flex items-center justify-between mb-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center shadow-sm ring-1 ring-slate-200/50 dark:ring-slate-700/50 transform group-hover:scale-110 transition-transform duration-500 ease-out">
                <Icon icon={icon} className="h-5 w-5 text-primary dark:text-primary/90" />
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <Icon icon="carbon:arrow-up-right" className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex flex-col grow">
            <h3 className="font-semibold text-base sm:text-lg text-slate-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary/90 transition-colors duration-300">
              {translationKey ? t(`Projects.items.${translationKey}.title`) : title}
            </h3>
            {(translationKey || description) && (
              <p className="text-sm text-slate-700 dark:text-muted-foreground mt-2 line-clamp-3">
                {translationKey ? t(`Projects.items.${translationKey}.description`) : description}
              </p>
            )}
          </div>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-slate-100 dark:bg-muted text-[10px] font-medium rounded-full text-slate-950 dark:text-foreground border border-slate-200/50 dark:border-border"
                >
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:text-muted-foreground">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Hover indicator line */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/80 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
        </div>
      </div>
    </Link>
  );
} 